import { useEffect, useRef, useState } from 'react';
import * as maplibregl from 'maplibre-gl';
import type { GeoJSONSource, Map as MapLibreMap, RasterTileSource } from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';
import { Protocol } from 'pmtiles';
import 'maplibre-gl/dist/maplibre-gl.css';
// MapLibre 6 otsib tööprotsessi faili oma mooduli URL-i kõrvalt
// (`./maplibre-gl-worker.mjs`). Vite ei näe seda staatiliselt ega emiteeri
// faili, seega jääks build'is 404 ja kaart ei renderdaks midagi. Laseme Vitel
// tööprotsessi ise pakendada ja anname valmis URL-i MapLibre'ile.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import {
  OVERLAY_LAYERS,
  baseStyle,
  overlayIsActive,
  type RadarFrame,
  type RasterLayerDef,
} from './basemaps';
import { addColorBase } from './colorBase';
import { addDarkBase } from './darkBase';
import { registerIcons } from './icons';
import { LAYER_ORDER } from './layerOrder';
import { POPUP_CLICK_LAYERS } from './popups';
import { addPlaceLabels } from './layers/placeLabels';
import { addOfficialDepth, removeOfficialDepthLayers } from './layers/officialDepth';
import type { Position } from '../lib/geolocation';
import type { DepthRiskSegment, RoutePlan, RouteWaypoint, TrackPoint } from '@seapro/shared';
import {
  addRouteLayers,
  ROUTE_LINE_LAYER,
  ROUTE_LINE_HIT_LAYER,
  ROUTE_WAYPOINT_HIT_LAYER,
  updateRouteLayers,
} from './layers/route';

export interface MapViewProps {
  center: [number, number];
  zoom: number;
  /** Millised overlay-kihid on sisse lülitatud. */
  activeOverlays: string[];
  radarFrame: RadarFrame;
  ownPosition: Position | null;
  selectedPoint: { lat: number; lon: number } | null;
  routeWaypoints: RouteWaypoint[];
  routeSegments: DepthRiskSegment[];
  routePlan: RoutePlan | null;
  trackPoints: TrackPoint[];
  routeEditing: boolean;
  routeEndpointPicking: boolean;
  selectedWaypointId: string | null;
  onReady(map: MapLibreMap): void;
  onMoveEnd(bbox: [number, number, number, number], zoom: number): void;
  onPick(lat: number, lon: number): void;
  onRouteSelect(id: string | null): void;
  onRouteMove(id: string, lat: number, lon: number): void;
  onRouteMoveStart(): void;
  onRouteInsert(index: number, lat: number, lon: number): void;
  onRouteSegmentSelect(index: number): void;
  onUserMove(): void;
}


/**
 * Suurim täpsus (meetrites), mille juures täpsusringi veel joonistame.
 * Päris GPS annab merel 5–30 m; kui number on suurem, on tegu Wi-Fi/IP
 * hinnanguga ja ring oleks eksitav mürakera üle poole kaardist.
 */
const ACCURACY_RING_MAX_M = 1000;
const DEPTH_CONTOUR_MIN_ZOOM = 7;
const DEPTH_SAMPLE_MIN_ZOOM = 12;
// Muuda väärtust, kui serveripoolne lõikus või allika sisu muutub. Päringu
// versioon väldib vana 24 h HTTP-cache'i näitamist pärast andmekihi uuendust.
const DEPTH_DISPLAY_REVISION = 'ee-fi-official-coverage-v1';
const EMPTY_FEATURE_COLLECTION: FeatureCollection = { type: 'FeatureCollection', features: [] };
const pmtilesProtocol = new Protocol();
let pmtilesProtocolRegistered = false;

function ensurePmtilesProtocol(): void {
  if (pmtilesProtocolRegistered) return;
  maplibregl.addProtocol('pmtiles', pmtilesProtocol.tile);
  pmtilesProtocolRegistered = true;
}

/** Web Mercatori resolutsioon: meetrit piksli kohta ekvaatoril zoomil 0. */
const EQUATOR_M_PER_PX = 156543.03392;

/**
 * Raadiuse konstant, millest MapLibre saab pikslid: raadius_px = unit * 2^zoom.
 * Laiuskraadi kokkusurumine arvestatakse siin ära, sest MapLibre'i avaldistes
 * pole koosinust.
 */
function accuracyRadiusUnit(accuracyM: number, lat: number): number {
  const metersPerPxAtZoom0 = EQUATOR_M_PER_PX * Math.cos((lat * Math.PI) / 180);
  return accuracyM / metersPerPxAtZoom0;
}

function nearestPlanSegment(plan: RoutePlan, lat: number, lon: number): number {
  const lonScale = Math.cos(lat * Math.PI / 180);
  const px = lon * lonScale;
  let bestIndex = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  plan.segments.forEach((segment, index) => {
    const ax = segment.from[0] * lonScale; const ay = segment.from[1];
    const bx = segment.to[0] * lonScale; const by = segment.to[1];
    const dx = bx - ax; const dy = by - ay;
    const length2 = dx * dx + dy * dy;
    const fraction = length2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (lat - ay) * dy) / length2));
    const distance = Math.hypot(px - (ax + fraction * dx), lat - (ay + fraction * dy));
    if (distance < bestDistance) { bestDistance = distance; bestIndex = index; }
  });
  return bestIndex;
}

function addRaster(map: MapLibreMap, def: RasterLayerDef, beforeId?: string): void {
  const sourceId = `src-${def.id}`;
  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, {
      type: 'raster',
      tiles: def.tiles,
      tileSize: def.tileSize ?? 256,
      attribution: def.attribution,
      ...(def.minzoom !== undefined ? { minzoom: def.minzoom } : {}),
      ...(def.maxzoom !== undefined ? { maxzoom: def.maxzoom } : {}),
      ...(def.bounds ? { bounds: def.bounds } : {}),
    });
  }
  if (!map.getLayer(def.id)) {
    map.addLayer(
      {
        id: def.id,
        type: 'raster',
        source: sourceId,
        paint: { 'raster-opacity': def.opacity ?? 1 },
      },
      beforeId,
    );
  }
}

/** EMODneti WFS-i GeoJSON kuvatakse päris vektorina, mitte rasterpildina. */
function addDepthContours(map: MapLibreMap, beforeId?: string): void {
  if (!map.getSource('src-depth-contours')) {
    map.addSource('src-depth-contours', {
      type: 'geojson',
      data: EMPTY_FEATURE_COLLECTION,
      attribution: '<a href="https://emodnet.ec.europa.eu/">EMODnet Bathymetry (CC BY 4.0)</a>',
    });
  }
  if (!map.getLayer('depth-contours')) {
    map.addLayer({
      id: 'depth-contours',
      type: 'line',
      source: 'src-depth-contours',
      minzoom: DEPTH_CONTOUR_MIN_ZOOM,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#0878a8',
        'line-opacity': 0.82,
        'line-width': [
          'interpolate', ['linear'], ['zoom'],
          DEPTH_CONTOUR_MIN_ZOOM, 0.8,
          14, 1.6,
        ],
      },
    }, beforeId);
  }
  if (!map.getLayer('depth-contour-labels')) {
    map.addLayer({
      id: 'depth-contour-labels',
      type: 'symbol',
      source: 'src-depth-contours',
      minzoom: DEPTH_CONTOUR_MIN_ZOOM,
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 350,
        'text-field': ['concat', ['to-string', ['get', 'elevation']], ' m'],
        'text-font': ['Open Sans Regular'],
        'text-size': 11,
        'text-keep-upright': true,
      },
      paint: {
        'text-color': '#075f86',
        'text-halo-color': 'rgba(235, 247, 251, 0.95)',
        'text-halo-width': 1.5,
      },
    }, beforeId);
  }
  if (!map.getSource('src-depth-samples')) {
    map.addSource('src-depth-samples', {
      type: 'geojson',
      data: EMPTY_FEATURE_COLLECTION,
      attribution: '<a href="https://emodnet.ec.europa.eu/">EMODnet DTM</a>',
    });
  }
  if (!map.getLayer('depth-sample-labels')) {
    map.addLayer({
      id: 'depth-sample-labels',
      type: 'symbol',
      source: 'src-depth-samples',
      minzoom: DEPTH_SAMPLE_MIN_ZOOM,
      layout: {
        'text-field': ['get', 'depthLabel'],
        'text-font': ['Open Sans Regular'],
        'text-size': [
          'interpolate', ['linear'], ['zoom'],
          DEPTH_SAMPLE_MIN_ZOOM, 10,
          16, 12,
        ],
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': '#174d67',
        'text-halo-color': 'rgba(238, 248, 251, 0.92)',
        'text-halo-width': 1.25,
      },
    }, beforeId);
  }
}

export function MapView({
  center,
  zoom,
  activeOverlays,
  radarFrame,
  ownPosition,
  selectedPoint,
  routeWaypoints,
  routeSegments,
  routePlan,
  trackPoints,
  routeEditing,
  routeEndpointPicking,
  selectedWaypointId,
  onReady,
  onMoveEnd,
  onPick,
  onRouteSelect,
  onRouteMove,
  onRouteMoveStart,
  onRouteInsert,
  onRouteSegmentSelect,
  onUserMove,
}: MapViewProps): React.ReactElement {
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const selectedPointMarker = useRef<maplibregl.Marker | null>(null);
  const [styleReady, setStyleReady] = useState(false);
  // Hoiame callback'id ref'is, et kaarti ei ehitataks iga renderi peale uuesti.
  const cb = useRef({ onReady, onMoveEnd, onPick, onRouteSelect, onRouteMove, onRouteMoveStart, onRouteInsert, onRouteSegmentSelect, onUserMove });
  cb.current = { onReady, onMoveEnd, onPick, onRouteSelect, onRouteMove, onRouteMoveStart, onRouteInsert, onRouteSegmentSelect, onUserMove };
  const routeEditingRef = useRef(routeEditing);
  routeEditingRef.current = routeEditing;
  const routeEndpointPickingRef = useRef(routeEndpointPicking);
  routeEndpointPickingRef.current = routeEndpointPicking;
  const routePlanRef = useRef(routePlan);
  routePlanRef.current = routePlan;
  const selectedWaypointRef = useRef(selectedWaypointId);
  selectedWaypointRef.current = selectedWaypointId;

  useEffect(() => {
    if (!container.current || mapRef.current) return;

    maplibregl.setWorkerUrl(maplibreWorkerUrl);
    ensurePmtilesProtocol();

    const map = new maplibregl.Map({
      container: container.current,
      style: baseStyle(),
      center: [center[1], center[0]],
      zoom,
      attributionControl: false,
      // Kaatris on kaardi pööramine kogemata liiga lihtne ja segav.
      pitchWithRotate: false,
      dragRotate: false,
      touchPitch: false,
      maxZoom: 18,
      minZoom: 3,
      // Telefoni puudutus liigub loomulikult mõne piksli ka siis, kui kasutaja
      // tahab vajutada. MapLibre'i vaikimisi 3 px piir tõlgendas sellise tapi
      // sageli lohistamiseks ega tekitanud üldse click-sündmust.
      clickTolerance: 8,
    });
    mapRef.current = map;

    // Arendusrežiimis anna kaart konsoolile KOHE, mitte alles pärast kihtide
    // loomist — muidu pole kaardi enda käivitusprobleeme kuidagi uurida.
    if (import.meta.env.DEV) {
      (window as unknown as { seaproMap?: MapLibreMap }).seaproMap = map;
    }

    map.on('error', (e) => {
      // MapLibre'i vaikimisi käitumine on vead alla neelata. Kaardikihtide
      // ja allikate vead on siin ainus koht, kus nad üldse nähtavaks saavad.
      console.error('[SeaPro] MapLibre:', e.error?.message ?? e);
    });

    map.touchZoomRotate.disableRotation();

    // Atribuudirida (OSM, Transpordiamet, MET jt) üleval paremal, mitte all.
    // All paremas nurgas on nüüd nupuvirn ja punktiprognoosi paneel; kokku
    // oli seal kolm asja ühe koha peal. Litsentsid nõuavad nähtavust, mitte
    // kindlat nurka — kokkupakitult on see ⓘ, mis avaneb puudutusel.
    map.addControl(
      new maplibregl.AttributionControl({ compact: true }),
      'top-right',
    );
    map.addControl(
      new maplibregl.ScaleControl({ maxWidth: 120, unit: 'nautical' }),
      'bottom-left',
    );
    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false, visualizePitch: false }),
      'top-right',
    );

    const initLayers = (): void => {
      registerIcons(map);
      // Mõlemad aluskaardid on vektorstiilid ja mõlemad lisatakse KOHE, aga
      // nähtav on korraga ainult üks. Nii on vahetus hetkeline ega nõua kihi
      // ehitamist lennult, ja järjekord on paigas enne andmekihte.
      // Peidetud kihtide paane MapLibre ei tõmba, seega teine ei maksa midagi.
      addColorBase(map);
      addDarkBase(map);
      addPlaceLabels(map);
      addRouteLayers(map);
      // Oma asukoha allikas luuakse kohe, et kiht oleks õiges järjekorras
      // olemas ka enne esimest GPS-fixi.
      map.addSource('own-position', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'own-position-accuracy',
        type: 'circle',
        source: 'own-position',
        // Näita täpsusringi ainult siis, kui see midagi ütleb.
        // IP-põhine asukoht annab täpsuseks kümneid kilomeetreid ja selline
        // ring kataks poole kaardist, ilma et kasutaja sellest midagi võidaks.
        filter: ['<', ['get', 'accuracy'], ACCURACY_RING_MAX_M],
        paint: {
          // Meetrid -> pikslid: 1 px = 156543.034 * cos(lat) / 2^zoom meetrit.
          // Laiuskraadist sõltuv osa on juba `radiusUnit`-i sisse arvutatud,
          // siin jääb ainult zoomi kordaja 2^zoom.
          //
          // MapLibre nõuab, et ['zoom'] esineks AINULT interpolate/step
          // avaldise tipptasemel — `['*', x, ['^', 2, ['zoom']]]` visatakse
          // vaikselt kõrvale ja terve kiht jääb lisamata. Eksponentsiaalne
          // interpolatsioon alusega 2 annab täpselt sama tulemuse legaalselt.
          'circle-radius': [
            'interpolate', ['exponential', 2], ['zoom'],
            0, ['get', 'radiusUnit'],
            22, ['*', ['get', 'radiusUnit'], 4194304],
          ],
          'circle-color': '#3fb6d8',
          'circle-opacity': 0.14,
          'circle-stroke-color': '#3fb6d8',
          'circle-stroke-width': 1,
          'circle-stroke-opacity': 0.45,
        },
      });
      map.addLayer({
        id: 'own-position',
        type: 'circle',
        source: 'own-position',
        paint: {
          'circle-radius': 7,
          'circle-color': '#2f7fd1',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 3,
        },
      });

      setStyleReady(true);
      cb.current.onReady(map);
      emitMove(map);
    };

    const emitMove = (m: MapLibreMap): void => {
      const b = m.getBounds();
      cb.current.onMoveEnd(
        [b.getSouth(), b.getWest(), b.getNorth(), b.getEast()],
        m.getZoom(),
      );
    };

    map.on('load', () => {
      // MapLibre neelab `load` käsitleja erandid vaikselt alla. Ilma selle
      // püüdmiseta jääb rakendus poolikuks — aluskaart on ekraanil, aga
      // andmekihte ei tule kunagi ja konsool vaikib. Üks vale avaldis
      // paint-reeglis maksis täpselt selle diagnoosimise.
      try {
        initLayers();
      } catch (err) {
        console.error('[SeaPro] kaardikihtide loomine ebaõnnestus:', err);
        // Anna rakendusele siiski teada, et kaart on olemas — parem
        // osaliselt töötav kaart kui täiesti tühi ekraan.
        setStyleReady(true);
        cb.current.onReady(map);
        emitMove(map);
      }
    });

    map.on('moveend', () => emitMove(map));
    map.on('dragstart', () => cb.current.onUserMove());

    let waypointGesture: { id: string; startX: number; startY: number; dragging: boolean } | null = null;
    let suppressRouteClick = false;
    const beginWaypointDrag = (e: maplibregl.MapLayerMouseEvent | maplibregl.MapLayerTouchEvent): void => {
      if (!routeEditingRef.current) return;
      const id = String(e.features?.[0]?.properties?.id ?? '');
      if (!id) return;
      e.preventDefault();
      waypointGesture = { id, startX: e.point.x, startY: e.point.y, dragging: false };
      map.dragPan.disable();
    };
    map.on('mousedown', ROUTE_WAYPOINT_HIT_LAYER, beginWaypointDrag);
    map.on('touchstart', ROUTE_WAYPOINT_HIT_LAYER, beginWaypointDrag);
    const moveWaypoint = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent): void => {
      if (!waypointGesture) return;
      if (!waypointGesture.dragging && Math.hypot(e.point.x - waypointGesture.startX, e.point.y - waypointGesture.startY) >= 6) {
        waypointGesture.dragging = true;
        cb.current.onRouteSelect(waypointGesture.id);
        cb.current.onRouteMoveStart();
      }
      if (waypointGesture.dragging) cb.current.onRouteMove(waypointGesture.id, e.lngLat.lat, e.lngLat.lng);
    };
    map.on('mousemove', moveWaypoint);
    map.on('touchmove', moveWaypoint);
    const endWaypointDrag = (): void => {
      if (!waypointGesture) return;
      cb.current.onRouteSelect(waypointGesture.id);
      waypointGesture = null;
      suppressRouteClick = true;
      map.dragPan.enable();
      window.setTimeout(() => { suppressRouteClick = false; }, 0);
    };
    map.on('mouseup', endWaypointDrag); map.on('touchend', endWaypointDrag);
    map.on('click', ROUTE_LINE_HIT_LAYER, (e) => {
      if (suppressRouteClick || routeEndpointPickingRef.current) return;
      if (routeEditingRef.current) {
        if (map.queryRenderedFeatures(e.point, { layers: [ROUTE_WAYPOINT_HIT_LAYER] }).length > 0) return;
        const index = Number(e.features?.[0]?.properties?.segmentIndex);
        if (Number.isInteger(index)) cb.current.onRouteInsert(index + 1, e.lngLat.lat, e.lngLat.lng);
        return;
      }
      if (!routePlanRef.current) return;
      const visible = map.queryRenderedFeatures(e.point, { layers: [ROUTE_LINE_LAYER] });
      const segment = visible.find((feature) => Number(feature.properties?.segmentIndex) >= 0);
      const visibleIndex = Number(segment?.properties?.segmentIndex);
      const index = Number.isInteger(visibleIndex) && visibleIndex >= 0
        ? visibleIndex
        : nearestPlanSegment(routePlanRef.current, e.lngLat.lat, e.lngLat.lng);
      if (Number.isInteger(index) && index >= 0) cb.current.onRouteSegmentSelect(index);
    });

    map.on('click', (e) => {
      if (routeEndpointPickingRef.current) {
        cb.current.onPick(e.lngLat.lat, e.lngLat.lng);
        return;
      }
      // Kui klikk tabas mõnda andmekihti (jaam, laev), tegeleb sellega
      // vastav kihi enda käsitleja — siin ei tohi punktipaneeli avada.
      if (routeEditingRef.current) {
        if (suppressRouteClick) return;
        const pointHit = map.queryRenderedFeatures(e.point, { layers: [ROUTE_WAYPOINT_HIT_LAYER] });
        if (pointHit.length > 0) return;
        const lineHit = map.queryRenderedFeatures(e.point, { layers: [ROUTE_LINE_HIT_LAYER] });
        if (lineHit.length > 0) return;
        if (selectedWaypointRef.current) cb.current.onRouteSelect(null);
        else cb.current.onPick(e.lngLat.lat, e.lngLat.lng);
        return;
      }
      if (routePlanRef.current && map.queryRenderedFeatures(e.point, { layers: [ROUTE_LINE_HIT_LAYER] }).length > 0) return;
      const padding = window.matchMedia('(pointer: coarse)').matches ? 12 : 3;
      // Punktiprognoosi blokeerivad ainult päriselt klikitavad objektid.
      // LAYER_ORDER sisaldab ka kohanimesid, tuulenooli ja muid visuaalseid
      // kihte, mis tegid suure osa kaardist mobiilis näiliselt mitteklikitavaks.
      const clickableLayers = POPUP_CLICK_LAYERS.filter((id) => map.getLayer(id));
      const hits = clickableLayers.length > 0 ? map.queryRenderedFeatures([
        [e.point.x - padding, e.point.y - padding],
        [e.point.x + padding, e.point.y + padding],
      ], {
        layers: clickableLayers,
      }) : [];
      if (hits.length > 0) return;
      cb.current.onPick(e.lngLat.lat, e.lngLat.lng);
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // Ehitame kaardi täpselt korra; center/zoom on ainult algväärtused.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Overlay-kihtide sisse/välja lülitamine.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;

    for (const def of OVERLAY_LAYERS) {
      const wanted = overlayIsActive(def.id, activeOverlays);
      const effectiveDef = def.id === 'radar' ? radarFrame.def : def;
      const exists = Boolean(map.getLayer(def.id));

      if (wanted && effectiveDef) {
        // Sama radariallikas vahetab ajaliuguri liikumisel URL-i. `setTiles`
        // tühjendab vanad paanid ja paneb MapLibre'i valitud WMS kaadrit küsima.
        if (def.id === 'radar') {
          map.getSource<RasterTileSource>(`src-${def.id}`)?.setTiles(effectiveDef.tiles);
        }
      }

      if (wanted && effectiveDef && !exists) {
        // Lisa esimese andmekihi ETTE, et andmed jääksid alati peale.
        const before = LAYER_ORDER.find((id) => map.getLayer(id));
        addRaster(map, effectiveDef, before);
      } else if ((!wanted || !effectiveDef) && exists) {
        map.removeLayer(def.id);
      }
    }
  }, [activeOverlays, radarFrame, styleReady]);

  // Samasügavusjooned: Eesti ja Soome ametlik ühine PMTiles; EMODnet katab
  // ainult ametliku ühendmaski välise Läänemere.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;

    const wanted = activeOverlays.includes('depth-details');
    if (!wanted) {
      removeOfficialDepthLayers(map);
      if (map.getLayer('depth-sample-labels')) map.removeLayer('depth-sample-labels');
      if (map.getLayer('depth-contour-labels')) map.removeLayer('depth-contour-labels');
      if (map.getLayer('depth-contours')) map.removeLayer('depth-contours');
      return;
    }

    const before = LAYER_ORDER.find((id) => map.getLayer(id));
    addDepthContours(map, before);
    addOfficialDepth(
      map,
      // Versiooniparameeter väldib eelmise arhiivi 24 h Range-cache'i osi.
      new URL('/data/official-depth.pmtiles?v=ee-fi-lod-v2', window.location.href).href,
      before,
    );

    let controller: AbortController | null = null;
    const refresh = (): void => {
      const source = map.getSource<GeoJSONSource>('src-depth-contours');
      const sampleSource = map.getSource<GeoJSONSource>('src-depth-samples');
      if (!source || !sampleSource) return;
      if (map.getZoom() < DEPTH_CONTOUR_MIN_ZOOM) {
        source.setData(EMPTY_FEATURE_COLLECTION);
        sampleSource.setData(EMPTY_FEATURE_COLLECTION);
        return;
      }

      const bounds = map.getBounds();
      const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]
        .map((value) => value.toFixed(5))
        .join(',');
      controller?.abort();
      controller = new AbortController();
      const zoom = Math.floor(map.getZoom());
      fetch(
        `/api/depth-contours?bbox=${encodeURIComponent(bbox)}&zoom=${zoom}&revision=${DEPTH_DISPLAY_REVISION}`,
        { signal: controller.signal },
      )
        .then(async (response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return await response.json() as FeatureCollection;
        })
        .then((data) => source.setData(data))
        .catch((error: unknown) => {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          console.error('[SeaPro] samasügavusjoonte laadimine ebaõnnestus:', error);
        });

      if (map.getZoom() < DEPTH_SAMPLE_MIN_ZOOM) {
        sampleSource.setData(EMPTY_FEATURE_COLLECTION);
      } else {
        fetch(
          `/api/depth-samples?bbox=${encodeURIComponent(bbox)}&zoom=${zoom}&revision=${DEPTH_DISPLAY_REVISION}`,
          { signal: controller.signal },
        )
          .then(async (response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return await response.json() as FeatureCollection;
          })
          .then((data) => sampleSource.setData(data))
          .catch((error: unknown) => {
            if (error instanceof DOMException && error.name === 'AbortError') return;
            console.error('[SeaPro] mudelsügavuste laadimine ebaõnnestus:', error);
          });
      }
    };

    refresh();
    map.on('moveend', refresh);
    return () => {
      controller?.abort();
      map.off('moveend', refresh);
    };
  }, [activeOverlays, styleReady]);

  // Oma asukoha marker.
  useEffect(() => {
    const map = mapRef.current;
    if (!styleReady) return;
    const src = map?.getSource<GeoJSONSource>('own-position');
    if (!map || !src) return;

    src.setData({
      type: 'FeatureCollection',
      features: ownPosition
        ? [
            {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [ownPosition.lon, ownPosition.lat] },
              properties: {
                accuracy: ownPosition.accuracy,
                radiusUnit: accuracyRadiusUnit(ownPosition.accuracy, ownPosition.lat),
              },
            },
          ]
        : [],
    });
  }, [ownPosition, styleReady]);

  // Prognoosipunkt püsib kaardil nähtaval, kuni prognoos suletakse.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!selectedPoint) {
      selectedPointMarker.current?.remove();
      selectedPointMarker.current = null;
      return;
    }

    if (!selectedPointMarker.current) {
      const marker = new maplibregl.Marker({ color: '#e0a94f', scale: 0.85 })
        .setLngLat([selectedPoint.lon, selectedPoint.lat])
        .addTo(map);
      marker.getElement().classList.add('selected-point-marker');
      selectedPointMarker.current = marker;
    } else {
      selectedPointMarker.current.setLngLat([selectedPoint.lon, selectedPoint.lat]);
    }
  }, [selectedPoint]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    updateRouteLayers(map, routeWaypoints, routeSegments, routePlan, trackPoints, routeEditing, selectedWaypointId);
    map.getCanvas().style.cursor = routeEditing || routeEndpointPicking ? 'crosshair' : '';
  }, [routeWaypoints, routeSegments, routePlan, trackPoints, routeEditing, routeEndpointPicking, selectedWaypointId, styleReady]);

  return <div ref={container} className="map-container" />;
}
